const {
  getElement,
  getElements,
  debounce,
  prefersReducedMotion,
  viewportSize,
} = window.BAO.Utils

export class ProductMedia extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['media', 'dot', 'link']
  }

  connectedCallback () {
    super.connectedCallback()
    this.currentProductUrl = this.externalEls?.imageswap?.element?.dataset?.productUrl
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.thumbsResizeObserver?.disconnect()
    cancelAnimationFrame(this.thumbsScrollFrame)
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.media?.exists && this.els?.dot?.exists) {
      this.handleMediaEntry = this.handleMediaEntry.bind(this)
      this.handlePageScroll = this.handlePageScroll.bind(this)
      this.debouncedApplyActiveIndex = debounce(
        this.applyActiveIndex.bind(this),
        250,
      )

      // eslint-disable-next-line compat/compat
      this.observer = new IntersectionObserver(this.handleMediaEntry, {
        threshold: this.intersectionThreshold,
      })

      this.els.media.elements.forEach(dotEl => {
        this.observer.observe(dotEl)
      })

      this.listeners.add(window, 'scroll', this.handlePageScroll)
    }

    if (this.els?.dot?.exists) {
      this.handleDotClick = this.handleDotClick.bind(this)
      this.els.dot.elements.forEach(dotEl =>
        this.listeners.add(dotEl, 'click', this.handleDotClick),
      )
    }

    if (this.els?.thumbs?.exists && this.els?.thumbsContainer?.exists) {
      this.updateThumbsLayout = this.updateThumbsLayout.bind(this)
      this.updateThumbsOverflow = this.updateThumbsOverflow.bind(this)

      this.updateThumbsLayout()

      this.listeners.add(
        this.els.thumbs.element,
        'scroll',
        this.updateThumbsOverflow,
      )

      // eslint-disable-next-line compat/compat
      this.thumbsResizeObserver = new ResizeObserver(this.updateThumbsLayout)
      this.thumbsResizeObserver.observe(this.els.media.elements[0])
      this.thumbsResizeObserver.observe(this.els.thumbs.element)

      if (this.els?.thumbsInner?.exists) {
        this.thumbsResizeObserver.observe(this.els.thumbsInner.element)
      }
    }

    if (this.els?.thumbsArrow?.exists) {
      this.handleThumbsArrowClick = this.handleThumbsArrowClick.bind(this)
      this.els.thumbsArrow.elements.forEach(arrowEl =>
        this.listeners.add(arrowEl, 'click', this.handleThumbsArrowClick),
      )
    }

    /*
      CS: Only commented out as this may still be used once Stuart has thought of a different idea.
      After uncommenting, add isTouchDevice back to line 6 window.BAO.Utils
    */

    /*
    if (
      this.externalEls?.imageswap?.exists &&
      this.els?.link?.exists &&
      !isTouchDevice()
    ) {
      this.handleImageSwap = this.handleImageSwap.bind(this)
      this.externalEls.imageswap.elements.forEach(imageSwapEl => {
        this.listeners.add(imageSwapEl, 'mouseenter', this.handleImageSwap)
        this.listeners.add(imageSwapEl, 'focusin', this.handleImageSwap)
      })
    }
    */
  }

  updateThumbsLayout () {
    this.updateThumbsAlignment()
    this.updateThumbsOverflow()
  }

  updateThumbsAlignment () {
    const leadMediaEl = this.els?.media?.elements?.[0]
    const thumbsInnerEl = this.els?.thumbsInner?.element
    if (!leadMediaEl || !thumbsInnerEl) return

    const leadMediaHeight = leadMediaEl.getBoundingClientRect().height
    const thumbsHeight = thumbsInnerEl.getBoundingClientRect().height
    const insetBlockStart = Math.max(0, (leadMediaHeight - thumbsHeight) / 2)

    this.style.setProperty(
      '--Media_ThumbsInsetBlockStart',
      `${insetBlockStart}px`,
    )
  }

  /** @type {IntersectionObserverCallback} */
  handleMediaEntry (entries, observer) {
    if (viewportSize() === 'xs') return

    const entry = entries.find(
      entry => entry.intersectionRatio >= observer.thresholds[0],
    )
    if (!entry) return

    this.pendingIndex = entry.target?.dataset?.index
    this.debouncedApplyActiveIndex()
  }

  handleDotClick (event) {
    const currentIndex = event?.currentTarget?.dataset?.index
    const currentMedia = this.els.media.elements.find(
      mediaEl => mediaEl.dataset?.index === currentIndex,
    )
    if (!currentMedia) return

    this.setActiveDot(currentIndex)
    this.pendingIndex = currentIndex
    this.debouncedApplyActiveIndex()

    if (viewportSize() !== 'xs') {
      currentMedia.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      })
    }
    event.currentTarget.blur()
  }

  handlePageScroll () {
    if (this.pendingIndex === undefined) return
    this.debouncedApplyActiveIndex()
  }

  applyActiveIndex () {
    if (this.pendingIndex === undefined) return

    this.setActiveDot(this.pendingIndex)
    this.scrollThumbsToIndex(this.pendingIndex)
  }

  setActiveDot (currentIndex) {
    this.els.dot.elements.forEach(dotEl =>
      dotEl.setAttribute(
        'aria-current',
        String(dotEl.dataset?.index === currentIndex),
      ),
    )
  }

  updateThumbsOverflow () {
    const thumbsEl = this.els?.thumbs?.element
    const containerEl = this.els?.thumbsContainer?.element
    if (!thumbsEl || !containerEl) return

    const maxScroll = thumbsEl.scrollHeight - thumbsEl.clientHeight
    const overflowThreshold = 2

    containerEl.classList.toggle(
      'prd-Media_Thumbs-overflowStart',
      thumbsEl.scrollTop > overflowThreshold,
    )
    containerEl.classList.toggle(
      'prd-Media_Thumbs-overflowEnd',
      thumbsEl.scrollTop < maxScroll - overflowThreshold,
    )
  }

  handleThumbsArrowClick (event) {
    const thumbsEl = this.els?.thumbs?.element
    if (!thumbsEl) return

    const direction =
      event?.currentTarget?.dataset?.direction === 'previous' ? -1 : 1

    cancelAnimationFrame(this.thumbsScrollFrame)
    thumbsEl.scrollBy({
      top: direction * thumbsEl.clientHeight * 0.8,
      behavior: 'smooth',
    })
    event.currentTarget.blur()
  }

  scrollThumbsToIndex (index) {
    const thumbsEl = this.els?.thumbs?.element
    if (!thumbsEl) return

    const thumbEl = this.els.dot.elements.find(
      dotEl => dotEl.dataset?.index === index && thumbsEl.contains(dotEl),
    )
    if (!thumbEl) return

    const fadeSize =
      parseFloat(
        getComputedStyle(thumbsEl).getPropertyValue('--Media_ThumbHeight'),
      ) || 54
    const thumbTop = thumbEl.offsetTop
    const thumbBottom = thumbTop + thumbEl.offsetHeight
    const visibleTop = thumbsEl.scrollTop + fadeSize
    const visibleBottom = thumbsEl.scrollTop + thumbsEl.clientHeight - fadeSize

    if (thumbTop >= visibleTop && thumbBottom <= visibleBottom) return

    this.animateThumbsScroll(
      thumbEl.offsetTop - (thumbsEl.clientHeight - thumbEl.offsetHeight) / 2,
    )
  }

  animateThumbsScroll (targetTop, duration = 1000) {
    const thumbsEl = this.els?.thumbs?.element
    if (!thumbsEl) return

    const startTop = thumbsEl.scrollTop
    const endTop = Math.max(
      0,
      Math.min(targetTop, thumbsEl.scrollHeight - thumbsEl.clientHeight),
    )
    const distance = endTop - startTop
    if (!distance) return

    const startTime = performance.now()
    const step = now => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 0.5 - Math.cos(progress * Math.PI) / 2

      thumbsEl.scrollTop = startTop + distance * eased
      if (progress < 1) this.thumbsScrollFrame = requestAnimationFrame(step)
    }

    cancelAnimationFrame(this.thumbsScrollFrame)
    this.thumbsScrollFrame = requestAnimationFrame(step)
  }

  async handleImageSwap (event) {
    const imageSwapEl = event.target.closest(
      this.externalEls?.imageswap?.selector,
    )
    if (
      !imageSwapEl ||
      !imageSwapEl.dataset?.productUrl ||
      imageSwapEl.dataset.productUrl === this.currentProductUrl
    ) {
      return
    }
    this.currentProductUrl = imageSwapEl.dataset.productUrl
    this.els.media.element.setAttribute('aria-busy', 'true')
    const newSlideLinkEl = await this.fetchSlideLinkHtml(
      imageSwapEl.dataset.productUrl,
    )

    /* Delay the render to allow for previous image to fade out slightly */
    setTimeout(() => {
      getElement(this.els.link.selector, { context: this })?.replaceWith(
        newSlideLinkEl,
      )
      /* Fix for ajax rendered blurry images in safari */
      getElements('img[srcset]', { context: this })?.forEach(imageEl => {
        // eslint-disable-next-line no-self-assign
        imageEl.outerHTML = imageEl.outerHTML
      })
      this.els.media.element.setAttribute('aria-busy', 'false')
    }, 400)
  }

  async fetchSlideLinkHtml (productUrl) {
    const response =
      this.storage?.[productUrl] ||
      (await window.BAO.sectionRenderer.fetchSingle(
        'product-media-slide-link',
        productUrl,
      ))
    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    this.updateStorage(productUrl, response)
    return getElement(this.els.link.selector, { context: newDOM })
  }

  updateStorage (key, value) {
    const storageObject = Object.create(this.storage)
    storageObject[key] = value
    this.storage = storageObject
  }

  set storage (storageObject) {
    this._storage = storageObject
  }

  get storage () {
    return this._storage || {}
  }

  get intersectionThreshold () {
    /** @type {HTMLElement[]} */
    const mediaElements = this.els.media.elements

    const mediaTallerThanViewport = mediaElements
      .map(media => {
        const { height } = media.getBoundingClientRect()

        return /** @type {const} */ ([media, height])
      })
      .filter(([_, height]) => height > window.innerHeight)

    if (mediaTallerThanViewport.length === 0) {
      return 1
    }

    const [, tallestHeight] = mediaTallerThanViewport.reduce(
      (currentTallest, mediaAndHeight) => {
        if (currentTallest === null) {
          return mediaAndHeight
        }

        const [, height] = mediaAndHeight

        return height > currentTallest[1] ? mediaAndHeight : currentTallest
      },
      null,
    )

    /**
     * The intersection observer still doesn't seem to trigger if we do `window.innerHeight / tallestHeight`,
     * adjusting it by .01 seems to work just fine though
     */
    return window.innerHeight / tallestHeight - 0.01
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
